import { Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { AdaptiveLayout } from "@/components/AdaptiveLayout";
import { AuthGuard } from "@/components/AuthGuard";
import { RoleGuard } from "@/components/RoleGuard";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ToastContainer } from "@/components/ui/Toast";
import { useMockAuth } from "@/config/privy";
import { usePrivySync } from "@/hooks/usePrivySync";
import { useTermsAcceptance } from "@/features/legal/termsAcceptance";
import { Analytics } from "@vercel/analytics/react";
import { scrubUrlFragment } from "@/lib/analyticsPrivacy";
import { ROUTES } from "@/lib/constants";
import { lazyRoute } from "@/lib/lazy-route";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { useChainOverridePolicy } from "@/hooks/useChainOverridePolicy";
import { usePreferredLanguageSync } from "@/hooks/usePreferredLanguageSync";

// Lazy-loaded route pages
const Home = lazyRoute(() => import("@/pages/home"));
const DashboardPage = lazyRoute(() => import("@/pages/DashboardPage"));
const DiscoverPage = lazyRoute(() => import("@/pages/DiscoverPage"));
const QuestDetailPage = lazyRoute(() => import("@/pages/QuestDetailPage"));
const QuestTaskChooserPage = lazyRoute(() => import("@/pages/QuestTaskChooserPage"));
const TaskDetailPage = lazyRoute(() => import("@/pages/TaskDetailPage"));
const WorkPage = lazyRoute(() => import("@/pages/WorkPage"));
const ChatsPage = lazyRoute(() => import("@/features/chats/ChatsPage"));
const ProfilePage = lazyRoute(() => import("@/pages/ProfilePage"));
const PublicProfilePage = lazyRoute(() => import("@/pages/PublicProfilePage"));
const CertificatePage = lazyRoute(() => import("@/pages/CertificatePage"));
const OrganizationPage = lazyRoute(() => import("@/pages/OrganizationPage"));
const OrganizationManagePage = lazyRoute(() => import("@/pages/OrganizationManagePage"));
const CreateOrganizationPage = lazyRoute(() => import("@/pages/CreateOrganizationPage"));
const JoinOrganizationPage = lazyRoute(() => import("@/pages/JoinOrganizationPage"));
const NotificationsPage = lazyRoute(() => import("@/pages/NotificationsPage"));
const AgentPage = lazyRoute(() => import("@/pages/AgentPage"));
const AdminPage = lazyRoute(() => import("@/pages/AdminPage"));
const OnboardingPage = lazyRoute(() => import("@/pages/onboarding/OnboardingPage"));
const OrgOnboardingPage = lazyRoute(() => import("@/pages/onboarding/OrgOnboardingPage"));
const GetReadyPage = lazyRoute(() => import("@/features/gettingStarted/GetReadyPage"));
const PublicBountiesPage = lazyRoute(() => import("@/pages/PublicBountiesPage"));
const AgentsPage = lazyRoute(() => import("@/pages/AgentsPage"));
const PublicBountyDetailPage = lazyRoute(() => import("@/pages/PublicBountyDetailPage"));
const NotFoundPage = lazyRoute(() => import("@/pages/NotFoundPage"));
const RegisterQuestsPage = lazyRoute(() => import("@/pages/RegisterQuestsPage"));
const RegisterWorkersPage = lazyRoute(() => import("@/pages/RegisterWorkersPage"));
const VolunteerLandingPage = lazyRoute(() => import("@/pages/VolunteerLandingPage"));
const WorkerLandingPage = lazyRoute(() => import("@/pages/WorkerLandingPage"));
const OrganizationLandingPage = lazyRoute(() => import("@/pages/OrganizationLandingPage"));
const OutreachLandingPage = lazyRoute(() => import("@/features/outreach/OutreachLandingPage"));
const OptOutPage = lazyRoute(() => import("@/features/outreach/OptOutPage"));
const EmailUnsubscribePage = lazyRoute(() => import("@/features/notifications/EmailUnsubscribePage"));
const PrivacyPolicyPage = lazyRoute(() => import("@/pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazyRoute(() => import("@/pages/TermsOfServicePage"));
// DEV-781: design preview for the shareable quest card. Its real destination is
// outreach (rendered to an image server-side); this route exists so the card can
// be reviewed against the reference before that pipeline is built.
//
// DEV MODE ONLY. It is an internal design harness carrying hardcoded sample
// business data, behind no auth guard, and it has no reason to be reachable by
// anyone who guesses the URL in production. `import.meta.env.DEV` is a literal
// at build time, so the whole branch — and the lazy import with it — is dropped
// from the production bundle rather than merely hidden.
const QuestShareCardPreview = import.meta.env.DEV
  ? lazyRoute(
      () => import("@/features/bounty/QuestShareCardPreview"),
      (m) => m.QuestShareCardPreview,
    )
  : null;

// Dev-only harness for the assistant's stock quest card (slot + budget
// steppers) — same DEV-literal gating as QuestShareCardPreview above.
const StockQuestCardPreview = import.meta.env.DEV
  ? lazyRoute(
      () => import("@/features/agent/StockQuestCardPreview"),
      (m) => m.StockQuestCardPreview,
    )
  : null;

function PageLoader() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-500" />
    </div>
  );
}

function PrivyBridge() {
  usePrivySync();
  // A fresh sign-in records acceptance of the 18+ / Terms / Privacy notice.
  useTermsAcceptance();
  // An admin's network override must not outlive their session -- see the hook.
  useChainOverridePolicy();
  return null;
}

export default function App() {
  usePreferredLanguageSync();
  return (
    <>
      {!useMockAuth && <PrivyBridge />}
      <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public routes. The landing brings its own nav and footer
              (PublicLayout's, in the 2026-10 brand); the other public pages
              keep the originals. */}
          <Route path="/" element={<Home />} />
          <Route path="/r" element={<OutreachLandingPage />} />
          <Route path="/unsubscribe" element={<OptOutPage />} />
          {/* DEV-802: notification email off-switch — a separate list from outreach. */}
          <Route path="/unsubscribe/email" element={<EmailUnsubscribePage />} />
          {QuestShareCardPreview && (
            <Route path="/quest-card-preview" element={<QuestShareCardPreview />} />
          )}

          {/* Adaptive routes — AppShell when auth'd, PublicLayout when anon */}
          <Route element={<AdaptiveLayout />}>
            <Route path="/volunteer" element={<VolunteerLandingPage />} />
            <Route path="/worker" element={<WorkerLandingPage />} />
            <Route path="/organizations" element={<OrganizationLandingPage />} />
            {/* Legacy redirect for old /ngo links */}
            <Route path="/ngo" element={<Navigate to="/organizations" replace />} />
            <Route path="/register/quests" element={<RegisterQuestsPage />} />
            <Route path="/register/workers" element={<RegisterWorkersPage />} />
            <Route path="/bounties" element={<PublicBountiesPage />} />
            {/* Copy-paste prompts for the reader's own AI tool. Public: the
                prompts only point at pages an outside AI can already read. */}
            <Route path="/agents" element={<AgentsPage />} />
            <Route path={ROUTES.PRIVACY} element={<PrivacyPolicyPage />} />
            <Route path={ROUTES.TERMS} element={<TermsOfServicePage />} />
            <Route path="/ai" element={<Navigate to="/agents" replace />} />
            <Route path="/bounty/:id" element={<PublicBountyDetailPage />} />
            {/* Adaptive, not guarded: the internal candidate email's "View the
                quest" CTA points here, and a recipient opening it on a phone
                they are not logged in on must still see the quest. Anonymous
                reads are already permitted by the anon_select_active_quests /
                anon_select_open_tasks policies, so a quest outside
                active|published|negotiating still renders not-found. The task
                page below stays guarded. */}
            <Route path="/quest/:id" element={<QuestDetailPage />} />
            {/* The short link, and the one that matters most in practice:
                quest-notify builds every notification CTA as `/q/<quest-id>`
                (supabase/functions/quest-notify/index.ts, matchInvite.ts), so
                this is the path in people's inboxes, Telegram and WhatsApp.
                It is an alias rather than a redirect because those links are
                already in the wild and must keep resolving in one hop.
                Same adaptive, unguarded placement as /quest/:id above — the
                reasoning there applies here verbatim. */}
            <Route path="/q/:id" element={<QuestDetailPage />} />
            {/* The task chooser behind a multi-task quest's "I'm interested".
                Unguarded like the quest page, and for a sharper reason: a
                visitor who logs in from the quest page lands HERE, and a
                brand-new account under AuthGuard would be bounced to the
                onboarding screen instead. Applying onboards them. */}
            <Route path="/q/:id/apply" element={<QuestTaskChooserPage />} />
            <Route path="/quest/:id/apply" element={<QuestTaskChooserPage />} />
            {/* Adaptive so a signed-out visitor gets the public navbar and its
                Sign in, not a bare page. The profile data itself is
                authenticated-only (public_profiles + get_profile_track_record);
                the page says so rather than rendering an empty profile. */}
            <Route path="/u/:handle" element={<PublicProfilePage />} />
            {/* Certificate verify page: open to anyone holding the link. */}
            <Route path="/c/:code" element={<CertificatePage />} />
            {/* Org pages are public directory pages (members need sign-in,
                which the page says). The invite landing is unguarded for the
                same reason as /q/:id/apply: AuthGuard would send a brand-new
                account to onboarding and drop the code. */}
            <Route path={ROUTES.ORG} element={<OrganizationPage />} />
            <Route path={ROUTES.ORG_JOIN} element={<JoinOrganizationPage />} />
          </Route>

          {/* Authenticated routes */}
          <Route element={<AuthGuard />}>
            {/* Onboarding — inside AuthGuard but outside AppShell */}
            <Route path="/onboarding/org" element={<OrgOnboardingPage />} />
            <Route path="/onboarding" element={<OnboardingPage />} />
            {/* Quest-readiness gate — inside AuthGuard, outside AppShell */}
            <Route path="/get-ready" element={<GetReadyPage />} />
            <Route element={<AppShell />}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/discover" element={<DiscoverPage />} />
              {/* `/t/<code>` is the shareable form (src/lib/shortId.ts); the
                  legacy two-UUID path redirects to it. */}
              <Route path="/t/:taskId" element={<TaskDetailPage />} />
              <Route path="/quest/:id/task/:taskId" element={<TaskDetailPage />} />
              <Route path="/work" element={<WorkPage />} />
              <Route path={ROUTES.CHATS} element={<ChatsPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path={ROUTES.ORGS_NEW} element={<CreateOrganizationPage />} />
              <Route path={ROUTES.ORG_MANAGE} element={<OrganizationManagePage />} />
              {/* Exchange now lives as a tab inside Discover. */}
              <Route path={ROUTES.EXCHANGE} element={<Navigate to="/discover" replace />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/assistant" element={<AgentPage />} />
              {StockQuestCardPreview && (
                <Route path="/stock-quest-preview" element={<StockQuestCardPreview />} />
              )}
              {/* Same page; the id in the path is what makes an open
                  conversation reloadable and linkable. Registered from the
                  constant, not a literal, because this is the one route whose
                  pattern is also matched at RUNTIME — useWorkspaceContext
                  calls useMatch(ROUTES.ASSISTANT_WORKSPACE) to decide whether
                  workspace mode turns on at all, so the two must be the same
                  string by construction. */}
              <Route path={ROUTES.ASSISTANT_WORKSPACE} element={<AgentPage />} />
              {/* Admin routes */}
              <Route element={<RoleGuard requiredRole="admin" />}>
                <Route path="/admin" element={<AdminPage />} />
              </Route>
              {/* Legacy redirect */}
              <Route path="/claw" element={<Navigate to="/assistant" replace />} />
            </Route>
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      </ErrorBoundary>
      <ToastContainer />
      {/*
        The outreach convert token is in the URL hash and IS the credential, so
        the page URL cannot be sent verbatim. beforeSend runs on every event.
      */}
      <Analytics beforeSend={(event) => ({ ...event, url: scrubUrlFragment(event.url) })} />
      <SpeedInsights />
    </>
  );
}
